<script lang="ts" setup>
import { TabComp, ContentComp, DetailComp, InputBool } from '@/components';
import { useEventCommonStore, useEventCommonStoreRef, useEventStore, useGuiStore, useToastStore } from '@/stores';
import { ref, computed } from 'vue';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import { Group, CategoryGroupConnection, Category } from '@/objects/generated';
import { BaseObjDao } from '@/dao/BaseObjDao';
import { featureIcons, type GroupId } from '@/.';
import { useTaskLoader } from '@/composeables/loaders';
import type { FieldAlertMessage } from '@/objects';

const props = defineProps<{
    id: GroupId;
}>();

const { AddLoader, LoadState } = useTaskLoader();
const comStore = useEventCommonStore();
const guiStore = useGuiStore();
const eventStore = useEventStore();
const group = ref<Group | null>(null);

AddLoader(
    async () => {
        group.value = await BaseObjDao.LoadOrCreateSingle(Group, props.id, eventStore.eventID);
    },
    'group.loading_group',
    'fa-user-check',
);

AddLoader(
    async () => {
        await comStore.EnsureLoaded(['Categories', 'Races']);
    },
    'group.loading_races',
    featureIcons.CATEGORY,
);

const { Categories } = useEventCommonStoreRef();

const changed = computed(() => (group.value?.EntityState ?? 'Unchanged') !== 'Unchanged');

const detailComp = ref<{ messages: FieldAlertMessage[] } | null>(null);

const isCategoryInGroup = (category: Category) =>
    group.value?.CategoryGroupConnection.some(
        (pk: CategoryGroupConnection) => pk.EntityState != 'Deleted' && pk.CategoryID == category.ID,
    ) || false;

const isIncludedChanged = (checked: boolean, category: Category) => {
    // find the one we already have
    const existingCategoryGroupConnection = group.value?.CategoryGroupConnection.find((pg) => pg.CategoryID == category.ID);

    // If we are now checked, we should add the connection
    if (checked) {
        if (!existingCategoryGroupConnection) {
            // Create it
            const npk = CategoryGroupConnection.FromJsValues({
                EntityState: 'Added',
                CategoryID: category.ID,
                GroupID: group.value!.ID,
                EventID: eventStore.eventID,
            });
            group.value?.CategoryGroupConnection.push(npk);
        } else {
            if (existingCategoryGroupConnection.EntityState == 'Deleted') {
                existingCategoryGroupConnection.DeleteUndo();
            }
        }
    }
    // If we are not checked we should remove the connection
    else {
        if (!existingCategoryGroupConnection) {
            // This should be unreachable, we are deleting one that exists
            return;
        }

        if (!group.value) {
            // Also unreachable, but it makes the linter happy
            return;
        }

        // If its Added, it hasnt been saved anywhere so we can just remove it entierly
        if (existingCategoryGroupConnection.EntityState === 'Added') {
            group.value.CategoryGroupConnection = group.value.CategoryGroupConnection.filter((c) => c != existingCategoryGroupConnection);
        } else {
            // mark it as deleted
            existingCategoryGroupConnection.Delete();
        }
    }
};

const tabs = new TabContainer();
tabs.AddTab(new TabDefinition('group-general', 'group.general', featureIcons.GROUPS));
tabs.AddTab(new TabDefinition('group-group', 'group.categories', featureIcons.CATEGORY));

defineExpose<DetailViewExpose<Group>>({
    val: computed(() => group.value),
    title: computed(() => group.value?.Name ?? ''),
    changed: changed,
    messages: computed(() => (detailComp.value !== null ? detailComp.value!.messages : [])),
});
</script>
<template>
    <ContentComp :load-state="LoadState">
        <TabComp
            :tabs="tabs"
            v-if="group != null"
            tab-body-classes="mt-3"
        >
            <template #tab-group-general>
                <DetailComp
                    :gui="guiStore.Group"
                    v-model="group"
                    rel="detailComp"
                />
            </template>
            <template #tab-group-group>
                <ContentComp>
                    <div class="row row-cols-3">
                        <InputBool
                            v-for="g in Categories"
                            :key="g.ID"
                            class="col mt-2"
                            :label="g.Name"
                            v-bind:model-value="isCategoryInGroup(g)"
                            @update:model-value="(checked) => isIncludedChanged(checked, g)"
                        ></InputBool>
                    </div>
                </ContentComp>
            </template>
        </TabComp>
    </ContentComp>
</template>
